import React, { useState, useEffect } from 'react';
import { Users, Building2, Star, CreditCard, TrendingUp, AlertTriangle } from 'lucide-react';

const Dashboard = () => {
  const [stats, setStats] = useState({
    totalUsers: 0,
    totalProviders: 0,
    pendingApprovals: 0,
    totalReviews: 0,
    todayRevenue: 0,
    activeBookings: 0,
    newRegistrations: 0,
    flaggedReviews: 0
  });

  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchDashboardData();
  }, []);

  const fetchDashboardData = async () => {
    try {
      // Simulate API call with mock data for now
      setTimeout(() => {
        setStats({
          totalUsers: 1247,
          totalProviders: 189,
          pendingApprovals: 12,
          totalReviews: 3456,
          todayRevenue: 15680.50,
          activeBookings: 87,
          newRegistrations: 23,
          flaggedReviews: 3
        });
        setLoading(false);
      }, 1000);
    } catch (error) {
      console.error('Error fetching dashboard data:', error);
      setLoading(false);
    }
  };

  const StatCard = ({ title, value, icon: Icon, change, color = "blue", suffix = "" }) => (
    <div className={`card ${color === 'green' ? 'stat-card-green' : color === 'orange' ? 'stat-card-orange' : color === 'red' ? 'stat-card-red' : 'stat-card'}`}>
      <div className="flex items-center justify-between">
        <div>
          <p className="text-sm font-medium opacity-80">{title}</p>
          <p className="text-2xl font-bold mt-1">
            {loading ? '...' : `${value.toLocaleString()}${suffix}`}
          </p>
          {change && (
            <p className="text-sm mt-1 opacity-80">
              {change > 0 ? '+' : ''}{change}% from last month
            </p>
          )}
        </div>
        <Icon className="h-8 w-8 opacity-80" />
      </div>
    </div>
  );

  const RecentActivity = () => (
    <div className="card">
      <h3 className="text-lg font-semibold mb-4">Recent Activity</h3>
      <div className="space-y-3">
        {[
          { type: 'user', message: 'New user registered: Jan Novák', time: '2 minutes ago' },
          { type: 'provider', message: 'Provider application submitted: Praha Cleaning', time: '15 minutes ago' },
          { type: 'review', message: 'New review flagged for moderation', time: '32 minutes ago' },
          { type: 'payment', message: 'Payment received: €89.50', time: '1 hour ago' },
          { type: 'booking', message: 'New booking confirmed', time: '2 hours ago' }
        ].map((activity, index) => (
          <div key={index} className="flex items-center space-x-3 p-3 bg-gray-50 rounded-lg">
            <div className={`h-2 w-2 rounded-full ${
              activity.type === 'user' ? 'bg-blue-500' :
              activity.type === 'provider' ? 'bg-green-500' :
              activity.type === 'review' ? 'bg-yellow-500' :
              activity.type === 'payment' ? 'bg-purple-500' : 'bg-indigo-500'
            }`}></div>
            <div className="flex-1">
              <p className="text-sm font-medium text-gray-900">{activity.message}</p>
              <p className="text-xs text-gray-500">{activity.time}</p>
            </div>
          </div>
        ))}
      </div>
    </div>
  );

  const SystemHealth = () => (
    <div className="card">
      <h3 className="text-lg font-semibold mb-4">System Health</h3>
      <div className="space-y-3">
        {[
          { service: 'API Server', status: 'healthy', uptime: '99.9%' },
          { service: 'Database', status: 'healthy', uptime: '99.8%' },
          { service: 'Payment Gateway', status: 'healthy', uptime: '99.7%' },
          { service: 'Email Service', status: 'warning', uptime: '98.2%' },
          { service: 'SMS Service', status: 'healthy', uptime: '99.5%' }
        ].map((service, index) => (
          <div key={index} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
            <div className="flex items-center space-x-3">
              <div className={`h-3 w-3 rounded-full ${
                service.status === 'healthy' ? 'bg-green-500' :
                service.status === 'warning' ? 'bg-yellow-500' : 'bg-red-500'
              }`}></div>
              <span className="text-sm font-medium">{service.service}</span>
            </div>
            <span className="text-sm text-gray-500">{service.uptime}</span>
          </div>
        ))}
      </div>
    </div>
  );

  return (
    <div className="space-y-6">
      {/* Welcome Header */}
      <div className="bg-gradient-to-r from-blue-600 to-blue-800 rounded-lg p-6 text-white">
        <h1 className="text-2xl font-bold">Welcome to ServiceFlow CZ Admin</h1>
        <p className="mt-2 opacity-90">Monitor and manage your platform from this central dashboard</p>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <StatCard
          title="Total Users"
          value={stats.totalUsers}
          icon={Users}
          change={12}
          color="blue"
        />
        <StatCard
          title="Active Providers"
          value={stats.totalProviders}
          icon={Building2}
          change={8}
          color="green"
        />
        <StatCard
          title="Pending Approvals"
          value={stats.pendingApprovals}
          icon={AlertTriangle}
          change={-5}
          color="orange"
        />
        <StatCard
          title="Total Reviews"
          value={stats.totalReviews}
          icon={Star}
          change={15}
          color="blue"
        />
      </div>

      {/* Second Row Stats */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <StatCard
          title="Today's Revenue"
          value={stats.todayRevenue}
          icon={CreditCard}
          change={7}
          color="green"
          suffix=" Kč"
        />
        <StatCard
          title="Active Bookings"
          value={stats.activeBookings}
          icon={TrendingUp}
          change={22}
          color="blue"
        />
        <StatCard
          title="New Registrations"
          value={stats.newRegistrations}
          icon={Users}
          change={45}
          color="green"
        />
        <StatCard
          title="Flagged Reviews"
          value={stats.flaggedReviews}
          icon={AlertTriangle}
          change={-12}
          color="red"
        />
      </div>

      {/* Content Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <RecentActivity />
        <SystemHealth />
      </div>

      {/* Quick Actions */}
      <div className="card">
        <h3 className="text-lg font-semibold mb-4">Quick Actions</h3>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <button className="btn-primary flex items-center justify-center space-x-2">
            <Users className="h-4 w-4" />
            <span>Manage Users</span>
          </button>
          <button className="btn-success flex items-center justify-center space-x-2">
            <Building2 className="h-4 w-4" />
            <span>Approve Providers</span>
          </button>
          <button className="btn-secondary flex items-center justify-center space-x-2">
            <Star className="h-4 w-4" />
            <span>Review Reports</span>
          </button>
          <button className="btn-danger flex items-center justify-center space-x-2">
            <AlertTriangle className="h-4 w-4" />
            <span>System Alerts</span>
          </button>
        </div>
      </div>
    </div>
  );
};

export default Dashboard;